import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, Building, Tag, ArrowRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { caseStudies } from '../lib/data'

const CaseStudyDetailPage = () => {
  const { slug } = useParams()
  const caseStudy = caseStudies.find(study => study.slug === slug)

  if (!caseStudy) {
    return (
      <div className="pt-32 pb-16">
        <div className="container-max text-center">
          <h1 className="text-4xl font-bold text-dsr-text mb-6">Case Study Not Found</h1>
          <Link to="/case-studies" className="btn-primary">
            Back to Case Studies
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{caseStudy.title} - DevSecRoot Case Study</title>
        <meta name="description" content={caseStudy.description} />
        <link rel="canonical" href={`https://devsecroot.com/case-studies/${caseStudy.slug}`} />
      </Helmet>

      <section className="pt-32 pb-16">
        <div className="container-max">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/case-studies" 
              className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Case Studies</span>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex justify-center mb-6">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                caseStudy.category === 'security' 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                {caseStudy.category}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              {caseStudy.title}
            </h1>
            
            <p className="text-xl text-dsr-text/70 max-w-3xl mx-auto mb-8">
              {caseStudy.description}
            </p>

            <div className="flex flex-wrap justify-center items-center gap-6 text-dsr-text/60">
              <div className="flex items-center space-x-2">
                <Building className="w-5 h-5 text-dsr-accent" />
                <span>{caseStudy.client}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-dsr-accent" />
                <span>{new Date(caseStudy.publishedAt).toLocaleDateString()}</span>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 space-y-8"
            >
              {/* Content */}
              <div className="glass-card-hover p-8">
                <div className="prose prose-invert max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight]}
                    components={{
                      h1: ({ children }) => <h1 className="text-3xl font-bold text-dsr-text mb-6">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-2xl font-bold text-dsr-text mb-4 mt-8">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-xl font-semibold text-dsr-text mb-3 mt-6">{children}</h3>,
                      h4: ({ children }) => <h4 className="text-lg font-semibold text-dsr-text mb-2 mt-4">{children}</h4>,
                      p: ({ children }) => <p className="text-dsr-text/70 mb-4 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc list-inside text-dsr-text/70 mb-4 space-y-2">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside text-dsr-text/70 mb-4 space-y-2">{children}</ol>,
                      li: ({ children }) => <li className="text-dsr-text/70">{children}</li>,
                      code: ({ children, className }) => {
                        const isInline = !className
                        if (isInline) {
                          return <code className="bg-dsr-card px-2 py-1 rounded text-dsr-accent text-sm font-mono">{children}</code>
                        }
                        return (
                          <pre className="bg-dsr-card border border-white/10 rounded-lg p-4 overflow-x-auto mb-4">
                            <code className="text-dsr-text/70 font-mono text-sm">{children}</code>
                          </pre>
                        )
                      },
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-dsr-accent pl-4 italic text-dsr-text/60 mb-4">
                          {children}
                        </blockquote>
                      ),
                      strong: ({ children }) => <strong className="font-semibold text-dsr-text">{children}</strong>,
                      em: ({ children }) => <em className="italic text-dsr-text/80">{children}</em>,
                      a: ({ children, href }) => (
                        <a href={href} className="text-dsr-accent hover:text-cyan-400 underline transition-colors">
                          {children}
                        </a>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto mb-4">
                          <table className="min-w-full border border-white/10 rounded-lg">
                            {children}
                          </table>
                        </div>
                      ),
                      th: ({ children }) => (
                        <th className="border border-white/10 px-4 py-2 bg-dsr-card text-dsr-text font-semibold text-left">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border border-white/10 px-4 py-2 text-dsr-text/70">
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {caseStudy.content}
                  </ReactMarkdown>
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              {/* Technologies */}
              <div className="glass-card-hover p-6">
                <h3 className="text-lg font-semibold text-dsr-text mb-4">Technologies Used</h3>
                <div className="space-y-2">
                  {caseStudy.technologies.map((tech, index) => (
                    <span key={index} className="inline-block px-3 py-1 bg-dsr-accent/20 text-dsr-accent rounded-lg text-sm mr-2 mb-2">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="glass-card-hover p-6 text-center">
                <h3 className="text-lg font-semibold text-dsr-text mb-4">Ready to Get Started?</h3>
                <p className="text-dsr-text/70 mb-6">
                  Let's discuss how we can help with your project
                </p>
                <Link to="/contact" className="btn-primary w-full">
                  Start Your Project
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Related Case Studies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-20"
          >
            <h2 className="text-3xl font-bold text-dsr-text mb-8 text-center">
              Related Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {caseStudies
                .filter(study => study.id !== caseStudy.id)
                .slice(0, 3)
                .map((study) => (
                  <Link
                    key={study.id}
                    to={`/case-studies/${study.slug}`}
                    className="glass-card-hover overflow-hidden group"
                  >
                    <div className="h-48 bg-gradient-to-br from-dsr-accent/20 to-cyan-500/20 flex items-center justify-center">
                      <Tag className="w-16 h-16 text-dsr-accent" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          study.category === 'security' 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {study.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-dsr-text mb-3 group-hover:text-dsr-accent transition-colors">
                        {study.title}
                      </h3>
                      <p className="text-dsr-text/70 mb-4">{study.description}</p>
                      <div className="inline-flex items-center space-x-2 text-dsr-accent group-hover:text-cyan-400 transition-colors">
                        <span>Read case study</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default CaseStudyDetailPage
